import { useLocation } from 'react-router-dom';
import { AndieAgents } from '@/ai/enum/andie-agents.enum';
import { useCallback, useEffect, useRef } from 'react';
import { useAndieChatControls } from '@/providers/andie-chat-provider';

// Allows the navigate tool's DB write queue to flush before the destination
// agent's instructions() reads pendingHandoff from the database.
const HANDOFF_TRIGGER_DELAY_MS = 800;

export const AndieChatAgentSelector = () => {
    const { agentId, setAgentId, status, triggerHandoff, agentNavigationPendingRef } = useAndieChatControls();
    const { pathname } = useLocation();
    const agentChangedByNavRef = useRef(false);

    const triggerHandoffDelayed = useCallback(() => {
        setTimeout(() => triggerHandoff(), HANDOFF_TRIGGER_DELAY_MS);
    }, [triggerHandoff]);

    useEffect(() => {
        let agentName = AndieAgents.GENERAL;

        if (pathname.startsWith('/posts')) {
            agentName = AndieAgents.CONTENT;
        }

        if (pathname.startsWith('/dashboards')) {
            agentName = AndieAgents.INSIGHTS;
        }

        if (agentId !== agentName) {
            const wasAgentNav = agentNavigationPendingRef.current;
            agentNavigationPendingRef.current = false; // consume

            if (wasAgentNav) {
                if (status === 'ready') {
                    // Stream already ended before the URL updated — fire after delay
                    triggerHandoffDelayed();
                } else {
                    // Stream still in progress — wait for it to end
                    agentChangedByNavRef.current = true;
                }
            }
            setAgentId(agentName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, agentId]);

    // When the stream ends and the agent was switched by agent-initiated navigation,
    // fire the new agent with an empty message — it picks up the task from pendingHandoff in memory.
    useEffect(() => {
        if (status !== 'ready' || !agentChangedByNavRef.current) return;
        agentChangedByNavRef.current = false;
        triggerHandoffDelayed();
    }, [status, triggerHandoffDelayed]);

    return null;
};
